import { useQuery } from '@apollo/client';
import { EmailEditor } from '@nile/banyan/src/Components/Editors';
import { TEMPLATE_TYPES } from '@nile/banyan/src/Constants/notifications';
import { GET_NOTIFICATION_TEMPLATES } from '@nile/banyan/src/Graphql/Mutations/templates';
import {
  AIComponent,
  AccessControl,
  FieldTitle,
  getAIRephraseKeys,
  getTranslatedText,
  TICKET_SOURCE
} from '@nile/components';
import { APPLY_AI_PROMPT, AccountClient } from '@nile/graphql';
import { SVGIcon } from '@superops/super-icons';
import {
  Badge,
  Banner,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  Popover,
  Sheet,
  TextStyle
} from '@superops/ui-design-system';
import Tippy from '@tippyjs/react';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import React, { Component, useCallback, useState } from 'react';
import { Tooltip } from 'react-clone-tippy';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import ArticleSearchSheet from '@nile/rmm/src/modules/knowledge-base/components/ArticleSearchSheet';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  ListWrapper,
  Header,
  Template,
  SenderHolder
} from '../ActionContainer';

export function ReplyTemplateList(props) {
  const { loading, error, data } = useQuery(GET_NOTIFICATION_TEMPLATES, {
    client: AccountClient,
    variables: {
      notificationTemplateType:
        props?.notificationTemplateType || TEMPLATE_TYPES.reply
    },
    fetchPolicy: 'network-only'
  });
  if (loading) return <div>loading...</div>;
  if (error) return <div>error...</div>;
  const populateTemplateContent = (template) => {
    props.toggleSheetActive();
    props.populateTemplate(template);
  };
  return (
    <ListWrapper>
      <Header>Reply templates</Header>
      {data.getNotificationTemplates.map((el) => {
        return (
          <Template onClick={populateTemplateContent.bind(this, el)}>
            <FieldTitle>{el.name}</FieldTitle>
          </Template>
        );
      })}
    </ListWrapper>
  );
}

export function HiddenActions(props) {
  const [ccVisible, setCcVisible] = useState(false);
  const [bccVisible, setBccVisible] = useState(false);
  const [sheetActive, setSheetActive] = useState(false);

  const toggleSheetActive = useCallback(
    () => setSheetActive((active) => !active),
    []
  );

  const populateTemplate = (template) => {
    props.updateTemplateContent(template);
  };

  const handleCc = () => {
    setCcVisible(true);
    props.updateState('showCc', true);
  };

  const handleBcc = () => {
    setBccVisible(true);
    props.updateState('showBcc', true);
  };

  return (
    <SenderHolder>
      <div className="flex g10">
        {!ccVisible && (
          <div className="pointer" onClick={handleCc}>
            <TextStyle weight="Medium">CC</TextStyle>
          </div>
        )}
        {!bccVisible && (
          <div className="pointer" onClick={handleBcc}>
            <TextStyle weight="Medium">BCC</TextStyle>
          </div>
        )}
      </div>

      {!props?.globalConversationFlag && (
        <>
          <Button id="actionReplyTemplates" onClick={toggleSheetActive}>
            <SVGIcon name="ReplyBubble" />
          </Button>

          <Sheet open={sheetActive} onClose={toggleSheetActive}>
            <ReplyTemplateList
              populateTemplate={populateTemplate}
              toggleSheetActive={toggleSheetActive}
            />
          </Sheet>
        </>
      )}
    </SenderHolder>
  );
}

export const ToolbarActions = (props) => {
  const {
    pinChat,
    disabled,
    updateTemplateContent,
    ticketId,
    onArticleCopy,
    source
  } = props;
  const intl = useIntl();
  const flags = useFlags();

  const aiRephraseKeys = getAIRephraseKeys(intl);

  const [ccVisible, setCcVisible] = useState(false);
  const [bccVisible, setBccVisible] = useState(false);

  const handleCc = () => {
    setCcVisible(true);
    props.updateState('showCc', true);
  };

  const handleBcc = () => {
    setBccVisible(true);
    props.updateState('showBcc', true);
  };

  const [showKb, setShowKb] = useState(false);
  const toggleKBSheetActive = useCallback(
    () => setShowKb((prevState) => !prevState),
    []
  );

  const [sheetActive, setSheetActive] = useState(false);
  const toggleSheetActive = useCallback(
    () => setSheetActive((active) => !active),
    []
  );

  const populateTemplate = (template) => {
    updateTemplateContent(template);
  };

  return (
    <div>
      <div className="flex g10 actr jend mb10">
        <div>
          {props?.showAIRephrase && (
            <AIComponent
              editorRef={props?.editorRef}
              onCopy={props?.onAIRephraseCopy}
              type="REPHRASE"
              hideButtonText
              prompts={props?.prompts || []}
              data={{
                Api: APPLY_AI_PROMPT,
                accessorKey: 'applyAIPrompt'
              }}
              sendFeedback={props?.sendFeedback}
              featureName="PROMPT_BUILDER"
              headingText={getTranslatedText(intl, 'common.rephrased.text')}
              headingIcon={'AIWand'}
              loadingText={aiRephraseKeys?.loadingText}
              successText={aiRephraseKeys?.successText}
              copyButtonText={getTranslatedText(intl, 'common.insert.text')}
              copySuccessText={getTranslatedText(intl, 'common.inserted')}
              mixpanelEventOnClick={{
                event: 'Rephrased with prompt - Ticket conversation reply',
                data: {
                  ticketId: ticketId
                }
              }}
              mixpanelEventOnCopy={{
                event:
                  'Copy and pasted rephrased content - Ticket conversation reply',
                data: {
                  ticketId: ticketId
                }
              }}
            />
          )}
        </div>
        {flags?.enableKnowledgeBaseRoleFeature && (
          <AccessControl
            roleToCheck="knowledgeBase.view"
            render={
              <div>
                <Tooltip
                  title={getTranslatedText(intl, 'common.knowledge.base')}
                  inertia
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                  className="tooltip"
                  position="bottom"
                >
                  <Button
                    id="actionKbTemplates"
                    plain
                    onClick={toggleKBSheetActive}
                    icon={<SVGIcon name="Notes" />}
                  ></Button>
                </Tooltip>

                <Sheet open={showKb} onClose={toggleKBSheetActive}>
                  <ArticleSearchSheet
                    toggleKBSheetActive={toggleKBSheetActive}
                    ticketId={ticketId}
                    insertLink={onArticleCopy}
                  />
                </Sheet>
              </div>
            }
          />
        )}
        <Tooltip
          title={getTranslatedText(intl, 'ticket.conversation.shift.screen')}
          inertia
          trigger="mouseenter"
          animation="shift"
          animateFill={false}
          className="tooltip"
          position="bottom"
        >
          <Button
            plain
            icon={<SVGIcon name="ShiftScreen" />}
            onClick={pinChat}
            disabled={disabled}
          ></Button>
        </Tooltip>

        {source !== TICKET_SOURCE.Communication && (
          <div>
            <Tooltip
              title={getTranslatedText(
                intl,
                'admin.templates.create.replyTemplate'
              )}
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
              className="tooltip"
              position="bottom"
            >
              <Button
                id="actionReplyTemplates"
                plain
                onClick={toggleSheetActive}
                icon={<SVGIcon name="ReplyBubble" />}
              ></Button>
            </Tooltip>

            <Sheet open={sheetActive} onClose={toggleSheetActive}>
              <ReplyTemplateList
                populateTemplate={populateTemplate}
                toggleSheetActive={toggleSheetActive}
              />
            </Sheet>
          </div>
        )}
      </div>

      {source !== TICKET_SOURCE.Communication && (
        <div className="flex g10 jend">
          {!ccVisible && (
            <div className="pointer" onClick={handleCc}>
              <TextStyle weight="Medium">CC</TextStyle>
            </div>
          )}
          {!bccVisible && (
            <div className="pointer" onClick={handleBcc}>
              <TextStyle weight="Medium">BCC</TextStyle>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
